import {useFormik} from 'formik'
import * as Yup from 'yup'
import {
  ICustomerLedgerFormType,
  ICustomerLedgerResponse,
  initCustomerLedgerFormValues,
} from './_model'
import {useRittApp} from '../../utils/contextProvider/RittAppProvider'
import OptimizedSelectBox from '../../utils/components/OptimizedSelectBox'
import FlatPickrDate from '../../utils/components/FlatPickrDate'
import {KTCard, KTCardBody} from '../../../_metronic/helpers'
import {BASE_URL} from '../config'
import {useMutation} from 'react-query'
import axios from 'axios'
import toast from 'react-hot-toast'
import {useState} from 'react'
import LedgerContent from './LedgerContent'
import {Helmet} from 'react-helmet-async'

const GET_CUSTOMER_LEDGER_URL = `${BASE_URL}/api/protect/getCustomerLedegerById`

const customerLedgerSchema = Yup.object().shape({
  username: Yup.string().required('Username is required'),
  sdate: Yup.string().required('Start date is required'),
  edate: Yup.string().required('End date is required'),
})

const CustomerLedger = () => {
  const {userInfo} = useRittApp()

  const [data, setData] = useState<ICustomerLedgerResponse | null>(null)

  const {
    mutateAsync: getCustomerLedger,
    isLoading,
    isError,
  } = useMutation({
    mutationFn: async (sendValue: Partial<ICustomerLedgerFormType>) => {
      const {userId, sdate, edate} = sendValue
      const response = await axios.get<ICustomerLedgerResponse>(GET_CUSTOMER_LEDGER_URL, {
        params: {
          userId,
          sdate,
          edate,
        },
      })
      const result = await response.data
      setData(result)
    },
    onSuccess: () => {
      toast.dismiss()
    },
  })

  const formik = useFormik<ICustomerLedgerFormType>({
    initialValues: initCustomerLedgerFormValues,
    validationSchema: customerLedgerSchema,
    onSubmit: async (values) => {
      try {
        toast.loading('Processing..')
        setData(null)
        const {username, ...sendValues} = values
        await getCustomerLedger(sendValues)
      } catch (error) {
        console.log(error)
      }
    },
  })

  const onSelectUser = (id: number) => formik.setFieldValue('userId', id)

  const resetForm = () => {
    formik.resetForm()
  }

  return (
    <div>
      <Helmet>
        <title>Customer Ledger</title>
      </Helmet>
      <h2 className='fs-2x mb-6 fw-normal'>Customer Ledger</h2>
      <KTCard>
        <KTCardBody>
          <form onSubmit={formik.handleSubmit}>
            <div className='row'>
              <div className='mb-4 col-12 col-md-6 col-lg-4'>
                <label className='form-label fs-6 fw-bold'>
                  <span className='required'>Username:</span>
                </label>

                <OptimizedSelectBox
                  formik={formik}
                  fieldValue='username'
                  placeholder='Select user'
                  setSelect={onSelectUser}
                  options={userInfo}
                />
              </div>

              <div className='mb-4 col-12 col-md-6 col-lg-4'>
                <label className='form-label fs-6 fw-bold'>
                  <span className='required'>Start Time:</span>
                </label>

                <div id='sdate' className=''>
                  <FlatPickrDate
                    placeholder='Select start time'
                    fieldName='sdate'
                    formik={formik}
                    container='sdate'
                  />
                </div>
              </div>

              <div className='mb-4 col-12 col-md-6 col-lg-4'>
                <label className='form-label fs-6 fw-bold'>
                  <span className='required'>End Time:</span>
                </label>

                <div id='edate' className=''>
                  <FlatPickrDate
                    placeholder='Select end time'
                    fieldName='edate'
                    formik={formik}
                    container='edate'
                  />
                </div>
              </div>
            </div>

            <div className='d-flex justify-content-start mt-6'>
              <button
                type='submit'
                className='btn btn-primary fw-bold px-6'
                data-kt-menu-dismiss='true'
                data-kt-user-table-filter='filter'
              >
                SEARCH
              </button>
            </div>
          </form>
        </KTCardBody>
      </KTCard>

      {isLoading ? (
        <div className='d-flex justify-content-center mt-10'>
          <div className='spinner-border' role='status'>
            <span className='visually-hidden'>Loading...</span>
          </div>
        </div>
      ) : (
        <>{data && <LedgerContent content={data} />}</>
      )}
    </div>
  )
}

export default CustomerLedger
